import { Link, useNavigate } from "react-router-dom";
import BoxCard from "shared/components/box/BoxCard.js";
import PopupPremiumInfo from "shared/components/popups/PopupPremiumInfo.js";
import "./MiscLandingContent.css";
import { isAssistant } from "shared/utils/env.js";

const MiscLandingContent = ({ props, flowUser, assistantUser, updateAssistantUser, logout, yScrollPosition }) => {
  const rootStyles = getComputedStyle(document.documentElement);
  const primaryColor = rootStyles.getPropertyValue("--primary-color").trim();
  const navigate = useNavigate();

  // Helper function to create conditional links
  const SmartLink = ({ to, children, className = "" }) => {
    const assistantPaths = ["/dash/", "/tools/", "/notification/", "/map", "/tracker/", "/listing-monitor"];
    const alwaysLocalPaths = ["/search", "/user/"];

    // Check if path should always be local
    const isAlwaysLocalPath = alwaysLocalPaths.some((path) => to === path || to.startsWith(path));

    // Check if path is assistant path (excluding always local paths)
    const isAssistantPath = !isAlwaysLocalPath && assistantPaths.some((path) => to.startsWith(path));

    if (isAlwaysLocalPath) {
      // Always use Link for /search and /user/** paths
      return (
        <Link to={to} className="primary-color">
          {children}
        </Link>
      );
    }

    if (isAssistant()) {
      if (isAssistantPath) {
        return (
          <Link to={to} className="main-blue-color">
            {children}
          </Link>
        );
      } else {
        return (
          <a href={`https://mfl-coach.com${to}`} target="_blank" rel="noopener noreferrer" className="main-pink-color">
            {children}
          </a>
        );
      }
    } else {
      if (!isAssistantPath) {
        return (
          <Link to={to} className="main-pink-color">
            {children}
          </Link>
        );
      } else {
        return (
          <a
            href={`https://mfl-assistant.com${to}`}
            target="_blank"
            rel="noopener noreferrer"
            className="main-blue-color"
          >
            {children}
          </a>
        );
      }
    }
  };

  return (
    <div id="MiscLandingContent" className="h-lg-100 w-100">
      <div className="d-flex h-lg-100 w-100 flex-column flex-lg-row flex-nowrap">
        <div
          className="d-flex flex-column flex-lg-grow-1 flex-lg-basis-50p h-100 pe-lg-3 pb-lg-3"
          style={{ minWidth: "0" }}
        >
          <div className="d-flex flex-column flex-lg-fill ps-lg-3">
            <div className="main-view d-flex flex-column flex-lg-row flex-lg-fill justify-content-center">
              <div className="d-flex flex-column align-self-center position-relative">
                <div className="landing-logo-pair">
                  <img
                    src={isAssistant() ? "/media/images/coach.png" : "/media/images/assistant.png"}
                    alt=""
                    className="landing-logo-background"
                  />
                  <img
                    src={isAssistant() ? "/media/images/assistant.png" : "/media/images/coach.png"}
                    alt={`MFL ${isAssistant() ? "Assistant" : "Coach"}`}
                    className="landing-logo-main"
                  />
                </div>

                <div className="d-flex justify-content-end mb-2 me-3">
                  <a
                    href={isAssistant() ? "https://mfl-coach.com" : "https://mfl-assistant.com"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn btn-link ${isAssistant() ? "main-pink-color" : "main-blue-color"}`}
                  >
                    Visit {isAssistant() ? "Coach" : "Assistant"}
                    <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                  </a>
                </div>

                <div>
                  <BoxCard
                    title={"Join the Premium Trial!"}
                    content={
                      <div className="d-flex flex-column flex-fill">
                        <div className="d-flex flex-fill justify-content-center">
                          <PopupPremiumInfo
                            trigger={
                              <button className="btn btn-premium">
                                <i className="bi bi-stars"></i> More info
                              </button>
                            }
                          />
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column flex-grow-1 flex-lg-basis-50p py-lg-3 pe-lg-3" style={{ minWidth: "0" }}>
          <BoxCard
            className="d-flex flex-fill overflow-auto h-100"
            content={
              <div className="d-flex flex-column flex-fill my-2">
                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">
                    How can I get global indicators about MFL?
                  </h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Consult the manager base indicator with{" "}
                      <SmartLink to="/dash/managers">Dashboard: Managers</SmartLink>
                    </li>
                    <li>
                      Check the marketplace activity with{" "}
                      <SmartLink to="/dash/marketplace">Dashboard: Marketplace</SmartLink>
                    </li>
                    <li>
                      Explore the player ecosystem with <SmartLink to="/dash/players">Dashboard: Players</SmartLink>
                    </li>
                    <li>
                      View the club landcape with <SmartLink to="/dash/clubs">Dashboard: Clubs</SmartLink>
                    </li>
                    <li>
                      Assess division strength with <SmartLink to="/dash/divisions">Dashboard: Divisions</SmartLink>
                    </li>
                    <li>
                      Open the <SmartLink to="/map">Interactive Map</SmartLink> with managers and clubs by location
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">
                    How to be effective on the market?
                  </h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Set up <SmartLink to="/notification/player">Notifications: Player</SmartLink> to stay informed
                      about player listings
                    </li>
                    <li>
                      Set up <SmartLink to="/notification/club">Notifications: Club</SmartLink> to stay informed about
                      club listings
                    </li>
                    <li>
                      Use the <SmartLink to="/listing-monitor">Listing monitor</SmartLink> to be first on new listings
                    </li>
                    <li>
                      Buy at proper prices using the{" "}
                      <SmartLink to="/tools/player-pricing">Tool: Player Pricing</SmartLink>
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">
                    How can I estimate a player gallery?
                  </h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Check your own player gallery <SmartLink to="/user/me/players">My HQ: Players</SmartLink>
                    </li>
                    <li>
                      <SmartLink to="/search">Search</SmartLink> for a manager, select 'HQ' button and the 'Players' tab
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">
                    How to prepare for the next season?
                  </h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Set up your dream team with the <SmartLink to="/squad-builder">Squad Builder</SmartLink>
                    </li>
                    <li>
                      Choose the most performing formation with{" "}
                      <SmartLink to="/formation-meta">Formation Meta</SmartLink>
                    </li>
                    <li>
                      Assess division strength with <SmartLink to="/dash/divisions">Dashboard: Divisions</SmartLink>
                    </li>
                    <li>
                      Contract at proper rates using the{" "}
                      <SmartLink to="/tools/contract-evaluation">Tool: Contract Evaluation</SmartLink>
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">
                    How to improve my performances?
                  </h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Have a look at your past performance ratings with{" "}
                      <SmartLink to="/user/me/performance">My HQ: Performance</SmartLink>
                    </li>
                    <li>
                      Counter opponents with <SmartLink to="/formation-meta">Formation Meta</SmartLink> insights
                    </li>
                    <li>
                      Use the <SmartLink to="/match-planner">Match Planner</SmartLink> to make the best tactical
                      decisions
                    </li>
                    <li>
                      Dig into the <SmartLink to="/tactic-analysis">Tactic Analysis</SmartLink> to aggregate match data
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">How can I estimate a player?</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Use the <SmartLink to="/tools/player-pricing">Tool: Player Pricing</SmartLink> with estimated
                      price, floor and past sales
                    </li>
                    <li>
                      <SmartLink to="/search">Search</SmartLink> for player and select the 'Pricing' view
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">How can I estimate a pack?</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Use the <SmartLink to="/tools/pack-pricing">Tool: Pack Pricing</SmartLink> with estimated price,
                      floor and past sales
                    </li>
                  </ul>
                </div>

                <div className="d-flex flex-column mb-2">
                  <h3 className="QuestionTitle text-lg font-bold py-2 mb-2 ps-2 w-100">Other things I should know?</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Activate the progression report via email with{" "}
                      <SmartLink to="/notification/report">Notification: Daily Report</SmartLink>
                    </li>
                    <li>
                      Check your legacy on a map with <SmartLink to="/user/me/map">My HQ: Map</SmartLink>
                    </li>
                  </ul>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MiscLandingContent;
